import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { FadeUp, Stagger, StaggerItem } from "./Motion";
import { FileText, ArrowUpRight, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ReportsGallery = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchReports = useCallback(async (attempt = 0) => {
    setError(false);
    if (attempt === 0) setLoading(true);
    try {
      const res = await axios.get(`${API}/reports`, { timeout: 12000 });
      if (Array.isArray(res.data) && res.data.length) {
        setReports(res.data);
        setError(false);
        setLoading(false);
      } else {
        throw new Error("empty");
      }
    } catch (e) {
      if (attempt < 5) {
        // backend may be cold-starting/restarting — retry with backoff
        setTimeout(() => fetchReports(attempt + 1), 1500 * (attempt + 1));
      } else {
        setError(true);
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchReports(0);
  }, [fetchReports]);

  return (
    <section id="reports" className="relative py-24 md:py-36 border-t border-ink/10 bg-white">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <FadeUp>
          <div className="flex items-center gap-4 md:gap-6 mb-16 flex-wrap">
            <span className="font-mono-cap text-ink/60">§ 05 — Sample Reports</span>
            <span className="hair-divider flex-1 min-w-8" />
            <span className="font-mono-cap text-ink/50">Full Report</span>
          </div>
        </FadeUp>

        <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
          <div className="col-span-12 lg:col-span-8">
            <FadeUp>
              <h2 className="font-display text-5xl md:text-7xl text-ink leading-[0.98]">
                See a full <br />
                <span className="italic text-teal">Business Health Report</span>.
              </h2>
            </FadeUp>
          </div>
          <div className="col-span-12 lg:col-span-4">
            <FadeUp delay={0.15}>
              <p className="font-sans text-ink/70 leading-relaxed">
                Each Business Health Report is a two-pillar assessment —
                Financial Health (0–500) and Operational Health (0–500), for an
                overall score out of 1000. Data sources are attributed inline
                (Surepass APIs, CIBIL Commercial, Account Aggregator, GSTN).
              </p>
            </FadeUp>
          </div>
        </div>

        {loading ? (
          <div className="mt-16 font-mono-cap text-ink/50" data-testid="reports-loading">
            Loading sample reports…
          </div>
        ) : error ? (
          <div className="mt-16 border border-ink/15 bg-white p-8 md:p-10" data-testid="reports-error">
            <div className="font-mono-cap text-ink/60">Reports temporarily unavailable</div>
            <p className="font-sans text-ink/70 mt-3 max-w-md">
              We couldn&apos;t load the sample reports just now. Please try again.
            </p>
            <button
              data-testid="reports-retry"
              onClick={() => fetchReports(0)}
              className="btn-ghost mt-6 inline-flex items-center gap-2"
            >
              <RefreshCw size={14} /> Retry
            </button>
          </div>
        ) : (
          <Stagger className={`mt-16 md:mt-20 grid gap-6 md:gap-8 ${reports.length === 1 ? "grid-cols-1 max-w-md mx-auto" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"}`}>
            {reports.map((r) => (
              <StaggerItem key={r.id}>
                <a
                  data-testid={`report-card-${r.id}`}
                  href={r.report_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block h-full border border-ink/12 bg-white p-7 md:p-8 transition-all duration-500 hover:border-teal/50 hover:-translate-y-1 hover:shadow-[0_24px_60px_-30px_rgba(18,58,51,0.25)]"
                >
                  <div className="flex items-start justify-between">
                    <span className="font-mono-cap text-teal">{r.sector}</span>
                    <FileText size={18} strokeWidth={1.5} className="text-ink/40 group-hover:text-teal transition-colors" />
                  </div>

                  <div className="mt-6 flex items-end justify-between gap-4">
                    <div className="font-display text-6xl leading-none text-teal">
                      {r.score}
                    </div>
                    <div className="pb-1 text-right">
                      <div className="font-mono-cap text-ink/70">{r.grade}</div>
                      <div className="font-sans text-xs text-ink/50 mt-1">
                        out of {r.score_max || 100}
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 pt-5 border-t border-ink/10">
                    <div className="font-display text-2xl text-ink leading-tight">{r.name}</div>
                    <div className="font-sans text-sm text-ink/60 mt-1">
                      {r.location} · {r.years} yrs
                    </div>
                  </div>

                  <div className="mt-6 space-y-2">
                    {r.pillars.map((p) => {
                      const pct = Math.round((p.value / (p.max || 100)) * 100);
                      return (
                        <div key={p.label} className="flex items-center gap-3">
                          <span className="font-mono-cap text-ink/45 text-[0.6rem] w-44 shrink-0 truncate">
                            {p.label}
                          </span>
                          <div className="score-bar flex-1">
                            <span style={{ width: `${pct}%` }} />
                          </div>
                          <span className="font-mono-cap text-ink/50 w-6 text-right">{p.value}</span>
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-7 inline-flex items-center gap-2 font-mono-cap text-ink group-hover:text-teal transition-colors">
                    View Full Report
                    <ArrowUpRight size={14} className="transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </div>
                </a>
              </StaggerItem>
            ))}
          </Stagger>
        )}

        <p className="mt-10 text-xs text-ink/45 font-sans max-w-2xl">
          Illustrative sample report based on synthetic data — not reflective of
          a real business or live scoring output.
        </p>
      </div>
    </section>
  );
};
